import React, { useMemo, useState, useCallback } from 'react';
import { trimEnd } from 'lodash';
import { useStandardCollator } from 'js/utils/string';
import { Button, Nav, NavItem, NavLink, Card, CardBody } from 'reactstrap';
import {
  PanelHeader,
  Icon,
  ReplaceLink,
  Loading as LoadingIndicator,
} from 'js/components';
import ConfirmModal from 'js/components/modal/ConfirmModal';
import { useRouteMatch } from 'react-router-dom';
import NoProductManagersCard from './NoProductManagersCard';
import ProductManagerForm from '../ProductManagerForm';
import ProductManager from './ProductManager';

import styles from './ProductManagersDashboard.module.scss';

function ProductManagersDashboard({
  hasLoaded,
  isLoading,
  productManagers,
  onAddProductManager,
  onUpdateProductManager,
  onSearch,
}) {
  const searchParams = new URLSearchParams(window.location.search);
  const match = useRouteMatch();
  const showArchived = searchParams.has('archived');

  const [isAddingProductManager, setIsAddingProductManager] = useState(false);
  const [productManagerToArchive, setProductManagerToArchive] = useState(null);

  const collator = useStandardCollator();
  const filteredProductManagers = useMemo(() => {
    return (
      productManagers
        ?.filter((productManager) => {
          return showArchived
            ? productManager.archived_at !== null
            : productManager.archived_at === null;
        })
        ?.sort(
          (a, b) =>
            collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
        ) ?? []
    );
  }, [productManagers, showArchived, collator]);

  const handleAddProductManager = useCallback(
    async (productManager) => {
      const result = await onAddProductManager(productManager);
      setIsAddingProductManager(false);
      return result;
    },
    [onAddProductManager]
  );

  const handleArchiveProductManager = useCallback(async (productManager) => {
    console.log('handleArchiveProductManager', productManager);
    setProductManagerToArchive(productManager);
    // await updateProductManager(productManager);
  }, []);

  const handleRestoreProductManager = useCallback(async (productManager) => {
    console.log('handleRestoreProductManager', productManager);
    // await updateProductManager(productManager);
  }, []);

  const toggleAddProductManager = useCallback(() => {
    setIsAddingProductManager(!isAddingProductManager);
  }, [isAddingProductManager]);

  const handleArchiveModalConfirm = useCallback(async () => {
    await onUpdateProductManager({
      id: productManagerToArchive.id,
      archived_state: 'archived',
    });
    setProductManagerToArchive(null);
  }, [onUpdateProductManager, productManagerToArchive]);

  return (
    <div>
      <PanelHeader size="flex">
        <div className="panel-body-no-label px-4">
          <div className="content-fluid">
            <div className="row">
              <div className="col-12 col-md-9">
                <h1>Product Managers</h1>
              </div>
              <div className="panel-actions col-12 col-md-3">
                <Button
                  color="primary"
                  className="create-link"
                  onClick={toggleAddProductManager}
                >
                  <Icon icon="plus" className="btn-icon-left" /> Add
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <Nav className="tab-nav nav-underline">
            <NavItem>
              <NavLink
                tag={ReplaceLink}
                active={!searchParams.has('archived')}
                data-view="active"
                to={`${trimEnd(match.url, '/')}`}
              >
                Active
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag={ReplaceLink}
                active={searchParams.has('archived')}
                data-view="archived"
                to={`${trimEnd(match.url, '/')}?archived`}
              >
                Archived
              </NavLink>
            </NavItem>
          </Nav>
        </div>
      </PanelHeader>
      <div className="content" style={{ position: 'relative' }}>
        {!hasLoaded && !filteredProductManagers?.length && (
          <Card>
            <CardBody>
              <p></p>
            </CardBody>
          </Card>
        )}

        {hasLoaded &&
          !filteredProductManagers?.length &&
          !isAddingProductManager && (
            <NoProductManagersCard
              type={showArchived ? 'archived' : 'default'}
              onAddProductManager={toggleAddProductManager}
            />
          )}

        {hasLoaded &&
          (filteredProductManagers?.length || isAddingProductManager) && (
            <Card>
              <CardBody>
                {isAddingProductManager && (
                  <ProductManagerForm
                    onAddProductManager={handleAddProductManager}
                    onCancel={() => setIsAddingProductManager(false)}
                    onSearch={onSearch}
                    existingProductManagers={productManagers ?? []}
                  />
                )}
                {filteredProductManagers?.length > 0 && (
                  <div className={styles['product-manager-list']}>
                    <div className={styles['product-manager-list-header']}>
                      <div>Name</div>
                      <div>User Groups</div>
                      <div>Capabilities</div>
                      <div></div>
                    </div>
                    {filteredProductManagers.map((productManager) => (
                      <ProductManager
                        key={productManager.id}
                        productManager={productManager}
                        onArchive={handleArchiveProductManager}
                        onRestore={handleRestoreProductManager}
                      />
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

        {isLoading && (
          <LoadingIndicator
            style={{ position: 'absolute', right: '25px', top: '10px' }}
          />
        )}
      </div>
      <ConfirmModal
        isOpen={!!productManagerToArchive}
        title="Archive product manager?"
        onConfirm={handleArchiveModalConfirm}
        onCancel={() => setProductManagerToArchive(null)}
        confirmButtonLabel="Archive"
      >
        <p>
          Please confirm that you want to archive{' '}
          <strong>
            {productManagerToArchive?.name || 'this product manager'}
          </strong>
          .
        </p>
      </ConfirmModal>
    </div>
  );
}

export default ProductManagersDashboard;
